/**
 * Command registry for the jump palette (components/CommandPalette). The
 * palette lists, in this order of sources: the pages it builds itself from
 * the job list (every token × lib/sections page, the chains' ledgers and
 * hooks, the global pages), the commands components register here while
 * mounted (`useCommands`), and every keyboard shortcut in lib/shortcuts'
 * registry. A view adds an entry with `useCommands([...])`, the same way it
 * adds a shortcut with `useShortcuts`.
 */
import { useEffect, useRef } from "react";
import { create } from "zustand";

export interface Command {
  /** stable across renders and reloads: the palette remembers recent ids */
  id: string;
  label: string;
  /** shown in grey after the label (a chain, a key, a section) */
  hint?: string;
  /** groups the list: "pages", "this page", "shortcuts", … */
  group: string;
  /** extra words to match besides the label, hint and group */
  keywords?: string[];
  run: () => void;
}

interface RegistryState {
  commands: Map<string, Command>;
  register: (key: string, c: Command) => void;
  unregister: (key: string) => void;
}

export const useCommandRegistry = create<RegistryState>((set) => ({
  commands: new Map(),
  register: (key, c) =>
    set((st) => {
      const next = new Map(st.commands);
      next.set(key, c);
      return { commands: next };
    }),
  unregister: (key) =>
    set((st) => {
      if (!st.commands.has(key)) return st;
      const next = new Map(st.commands);
      next.delete(key);
      return { commands: next };
    }),
}));

let seq = 0;

/** Register commands for the lifetime of the component. `run` always calls the latest closure. */
export function useCommands(commands: Command[], enabled = true): void {
  const register = useCommandRegistry((s) => s.register);
  const unregister = useCommandRegistry((s) => s.unregister);
  const latest = useRef(commands);
  useEffect(() => {
    latest.current = commands;
  });
  useEffect(() => {
    if (!enabled) return;
    const keys = commands.map((c, i) => {
      const key = `cmd-${++seq}`;
      register(key, { ...c, run: () => (latest.current[i] ?? c).run() });
      return key;
    });
    return () => keys.forEach(unregister);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, register, unregister, ...commands.map((c) => c.id + c.label)]);
}
